'use client'

import { useState, useEffect, useCallback } from 'react'
import { apiFetch } from '@/lib/api'
import { exportPerformanceExcel } from '@/lib/export-performance'
import { Button } from '@/components/ui/button'
import { DateInput } from '@/components/ui/date-input'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig,
} from '@/components/ui/chart'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, PieChart, Pie, Cell,
} from 'recharts'
import {
  ArrowLeft, Download, CalendarDays, Users, CheckCircle2,
  Clock, TrendingUp, BarChart3, AlertTriangle, ListTodo,
} from 'lucide-react'
import { format } from 'date-fns'
import type { PerformanceResponse, EmployeePerformance } from '@/types/performance'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { MonthlyReports } from './MonthlyReports'
import { PerfSettings } from './PerfSettings'
import { HolidayManager } from './HolidayManager'
import { CheckinRequests } from './CheckinRequests'
import { PageShell } from '@/components/ui/page-shell'
import { SectionHeader } from '@/components/ui/section-header'
import { GlassCard, GlassCardHeader, GlassCardTitle } from '@/components/ui/glass-card'
import { KpiTile } from '@/components/ui/kpi-tile'
import { StatusPill } from '@/components/ui/status-pill'
import { TabLoader, EmptyState } from '@/components/ui/loading'
import { PRIMARY_BTN } from '@/components/ui/button-styles'

const overviewChartConfig = {
  attendanceRate: { label: 'Attendance %', color: '#f97316' },
  completionRate: { label: 'Task Completion %', color: '#3b82f6' },
  avgHoursPerDay: { label: 'Avg Hours/Day', color: '#22c55e' },
} satisfies ChartConfig

const attendanceChartConfig = {
  workingHours: { label: 'Working Hours', color: '#22c55e' },
} satisfies ChartConfig

const taskPieConfig = {
  completed: { label: 'Completed', color: '#22c55e' },
  inProgress: { label: 'In Progress', color: '#3b82f6' },
  pending: { label: 'Pending', color: '#f59e0b' },
} satisfies ChartConfig

const hoursChartConfig = {
  hours: { label: 'Hours', color: '#8b5cf6' },
} satisfies ChartConfig

export function PerformanceAnalytics() {
  const [data, setData] = useState<PerformanceResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-01'))
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'))
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeePerformance | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const res = await apiFetch(`/api/performance?startDate=${startDate}&endDate=${endDate}`)
      const json = await res.json()
      setData(json)
    } catch (error) {
      console.error('Error fetching performance data:', error)
    } finally {
      setLoading(false)
    }
  }, [startDate, endDate])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleExport = () => {
    if (data) exportPerformanceExcel(data)
  }

  if (loading) {
    return (
      <PageShell>
        <SectionHeader
          icon={BarChart3}
          title='Performance Analytics'
          description='Daily aggregate across all interns'
        />
        <TabLoader />
      </PageShell>
    )
  }

  if (!data) {
    return (
      <PageShell>
        <SectionHeader
          icon={BarChart3}
          title='Performance Analytics'
          description='Daily aggregate across all interns'
        />
        <EmptyState
          icon={AlertTriangle}
          title='Failed to load performance data'
          description='The performance endpoint did not return any data. Try again in a moment.'
        />
      </PageShell>
    )
  }

  if (selectedEmployee) {
    return (
      <DetailView
        employee={selectedEmployee}
        data={data}
        onBack={() => setSelectedEmployee(null)}
      />
    )
  }

  const showInactive = localStorage.getItem('sasha_show_inactive_users') === 'true'
  const visibleEmployees = data.employees.filter((emp: any) => showInactive || emp.isActive !== false)

  const comparisonData = visibleEmployees.map(emp => ({
    name: emp.name.split(' ')[0],
    attendanceRate: emp.attendance.attendanceRate,
    completionRate: emp.tasks.completionRate,
    avgHoursPerDay: emp.dailyWork.avgHoursPerDay,
  }))

  const avgAttendance = visibleEmployees.length > 0
    ? Math.round(visibleEmployees.reduce((s, e) => s + e.attendance.attendanceRate, 0) / visibleEmployees.length)
    : 0
  const avgCompletion = visibleEmployees.length > 0
    ? Math.round(visibleEmployees.reduce((s, e) => s + e.tasks.completionRate, 0) / visibleEmployees.length)
    : 0
  const avgHours = visibleEmployees.length > 0
    ? Math.round(visibleEmployees.reduce((s, e) => s + e.dailyWork.avgHoursPerDay, 0) / visibleEmployees.length * 10) / 10
    : 0

  return (
    <PageShell>
      <Tabs defaultValue='daily' className='w-full'>
        <div className='overflow-x-auto -mx-2 px-2'>
          <TabsList className='w-max min-w-full bg-neutral-50 dark:bg-white/5 ring-1 ring-inset ring-neutral-200 dark:ring-white/10 backdrop-blur'>
            <TabsTrigger value='daily'>Daily</TabsTrigger>
            <TabsTrigger value='monthly'>Monthly</TabsTrigger>
            <TabsTrigger value='settings'>Settings</TabsTrigger>
            <TabsTrigger value='holidays'>Holidays</TabsTrigger>
            <TabsTrigger value='checkins'>Check-In Approvals</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value='daily' className="mt-5">
          <div className='space-y-5'>
            <SectionHeader
              icon={BarChart3}
              title="Performance Analytics"
              description="Daily aggregate across all interns"
              action={
                <Button onClick={handleExport} className={PRIMARY_BTN}>
                  <Download className='h-4 w-4 mr-2' />
                  Excel
                </Button>
              }
            />

            <GlassCard inset>
              <div className="p-4 md:p-5 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className='flex items-center gap-2'>
                  <label className='text-xs uppercase tracking-wider text-neutral-500 dark:text-white/50'>From</label>
                  <DateInput value={startDate} onChange={setStartDate} className='w-40' />
                </div>
                <div className='flex items-center gap-2'>
                  <label className='text-xs uppercase tracking-wider text-neutral-500 dark:text-white/50'>To</label>
                  <DateInput value={endDate} onChange={setEndDate} className='w-40' />
                </div>
              </div>
            </GlassCard>

            <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
              <KpiTile label='Total Interns' value={visibleEmployees.length} icon={Users} tone='orange' />
              <KpiTile label='Avg Attendance' value={avgAttendance} icon={CalendarDays} tone='green' format={(n) => `${n}%`} />
              <KpiTile label='Avg Task Completion' value={avgCompletion} icon={CheckCircle2} tone='blue' format={(n) => `${n}%`} />
              <KpiTile label='Avg Hours/Day' value={avgHours} icon={Clock} tone='violet' format={(n) => `${n}h`} />
            </div>

            {comparisonData.length > 0 && (
              <GlassCard>
                <GlassCardHeader>
                  <GlassCardTitle>Intern Comparison</GlassCardTitle>
                </GlassCardHeader>
                <ChartContainer config={overviewChartConfig} className='h-[350px] w-full'>
                  <BarChart data={comparisonData}>
                    <CartesianGrid strokeDasharray='3 3' stroke='rgba(255,255,255,0.1)' />
                    <XAxis dataKey='name' stroke='rgba(255,255,255,0.5)' fontSize={12} />
                    <YAxis stroke='rgba(255,255,255,0.5)' fontSize={12} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <ChartLegend content={<ChartLegendContent />} />
                    <Bar dataKey='attendanceRate' fill='var(--color-attendanceRate)' radius={[4, 4, 0, 0]} />
                    <Bar dataKey='completionRate' fill='var(--color-completionRate)' radius={[4, 4, 0, 0]} />
                    <Bar dataKey='avgHoursPerDay' fill='var(--color-avgHoursPerDay)' radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ChartContainer>
              </GlassCard>
            )}

            <GlassCard>
              <GlassCardHeader>
                <GlassCardTitle>Performance Summary</GlassCardTitle>
              </GlassCardHeader>
              {visibleEmployees.length === 0 ? (
                <EmptyState
                  icon={Users}
                  title='No employee data for this date range'
                  description='Widen the From / To range or check that employees have activity logged.'
                />
              ) : (
                <>
                  {/* Desktop table */}
                  <div className='hidden md:block overflow-x-auto'>
                    <table className='w-full text-sm min-w-[760px]'>
                      <thead>
                        <tr className='border-b border-neutral-200 dark:border-white/10'>
                          <th className='text-left py-3 px-4 text-neutral-500 dark:text-white/50 font-medium'>Name</th>
                          <th className='text-center py-3 px-4 text-neutral-500 dark:text-white/50 font-medium'>Attendance</th>
                          <th className='text-center py-3 px-4 text-neutral-500 dark:text-white/50 font-medium'>Tasks</th>
                          <th className='text-center py-3 px-4 text-neutral-500 dark:text-white/50 font-medium'>Avg Hours</th>
                          <th className='text-center py-3 px-4 text-neutral-500 dark:text-white/50 font-medium'>Consistency</th>
                          <th className='text-center py-3 px-4 text-neutral-500 dark:text-white/50 font-medium'>Availability</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleEmployees.map(emp => (
                          <tr
                            key={emp.userId}
                            className='border-b border-neutral-100 dark:border-white/5 hover:bg-neutral-50 dark:hover:bg-white/5 cursor-pointer'
                            onClick={() => setSelectedEmployee(emp)}
                          >
                            <td className='py-3 px-4'>
                              <p className='text-neutral-900 dark:text-white/90 font-medium'>{emp.name}</p>
                              <p className='text-xs text-neutral-400 dark:text-white/40'>{emp.position || emp.department || ''}</p>
                            </td>
                            <td className='text-center py-3 px-4'><RateBadge value={emp.attendance.attendanceRate} /></td>
                            <td className='text-center py-3 px-4'>
                              <span className='text-neutral-800 dark:text-white/80'>{emp.tasks.completed}/{emp.tasks.assigned}</span>
                              <span className='text-xs text-neutral-400 dark:text-white/40 ml-1'>({emp.tasks.completionRate}%)</span>
                            </td>
                            <td className='text-center py-3 px-4 text-neutral-800 dark:text-white/80'>{emp.dailyWork.avgHoursPerDay}h</td>
                            <td className='text-center py-3 px-4'><RateBadge value={emp.dailyWork.consistencyRate} /></td>
                            <td className='text-center py-3 px-4'><RateBadge value={emp.leaveWfh.availabilityRate} /></td>
                            {/* Removed a trailing ghost button that had no onClick — it
                                looked actionable but did nothing. */}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile cards */}
                  <div className='md:hidden space-y-3'>
                    {visibleEmployees.map(emp => (
                      <GlassCard key={emp.userId} interactive onClick={() => setSelectedEmployee(emp)}>
                        <div className='flex items-start justify-between gap-2'>
                          <div className='min-w-0'>
                            <p className='text-neutral-900 dark:text-white/90 font-medium truncate'>{emp.name}</p>
                            <p className='text-xs text-neutral-400 dark:text-white/40'>{emp.position || emp.department || ''}</p>
                          </div>
                          <TrendingUp className='h-4 w-4 text-orange-400' />
                        </div>
                        <div className='mt-3 grid grid-cols-3 gap-2 text-xs'>
                          <div>
                            <p className='text-neutral-400 dark:text-white/40'>Attend</p>
                            <RateBadge value={emp.attendance.attendanceRate} />
                          </div>
                          <div>
                            <p className='text-neutral-400 dark:text-white/40'>Tasks</p>
                            <p className='text-neutral-800 dark:text-white/80'>{emp.tasks.completed}/{emp.tasks.assigned}</p>
                          </div>
                          <div>
                            <p className='text-neutral-400 dark:text-white/40'>Hrs/Day</p>
                            <p className='text-neutral-800 dark:text-white/80'>{emp.dailyWork.avgHoursPerDay}h</p>
                          </div>
                        </div>
                      </GlassCard>
                    ))}
                  </div>
                </>
              )}
            </GlassCard>
          </div>
        </TabsContent>

        <TabsContent value='monthly' className="mt-5"><MonthlyReports /></TabsContent>
        <TabsContent value='settings' className="mt-5"><PerfSettings /></TabsContent>
        <TabsContent value='holidays' className="mt-5"><HolidayManager /></TabsContent>
        <TabsContent value='checkins' className="mt-5"><CheckinRequests /></TabsContent>
      </Tabs>
    </PageShell>
  )
}

function RateBadge({ value }: { value: number }) {
  const tone = value >= 80 ? 'green' : value >= 50 ? 'amber' : 'red'
  return <StatusPill tone={tone}>{value}%</StatusPill>
}

function DetailView({
  employee,
  data,
  onBack,
}: {
  employee: EmployeePerformance
  data: PerformanceResponse
  onBack: () => void
}) {
  const details = data.attendanceDetails[employee.userId] || []

  const attendanceChartData = details.map(d => ({
    date: d.date.slice(8) + '/' + d.date.slice(5, 7),
    workingHours: d.workingHours,
  }))

  const taskPieData = [
    { name: 'completed', value: employee.tasks.completed, fill: '#22c55e' },
    { name: 'inProgress', value: employee.tasks.inProgress, fill: '#3b82f6' },
    { name: 'pending', value: employee.tasks.pending, fill: '#f59e0b' },
  ].filter(d => d.value > 0)

  const weekMap: Record<string, number> = {}
  for (const d of details) {
    const dateObj = new Date(d.date)
    const dayOfWeek = dateObj.getDay()
    const diff = dateObj.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1)
    const ws = new Date(new Date(d.date).setDate(diff))
    const key = format(ws, 'dd/MM')
    if (!weekMap[key]) weekMap[key] = 0
    weekMap[key] += d.workingHours
  }

  const hoursChartData = Object.entries(weekMap).map(([week, hours]) => ({
    week,
    hours: Math.round(hours * 10) / 10,
  }))

  return (
    <PageShell>
      <div className='flex items-center gap-3'>
        <Button variant='ghost' onClick={onBack} className='text-neutral-600 dark:text-white/60 hover:text-neutral-900 dark:hover:text-white'>
          <ArrowLeft className='h-5 w-5' />
        </Button>
        <div>
          <h2 className='text-xl font-semibold text-neutral-900 dark:text-white/90'>{employee.name}</h2>
          <p className='text-sm text-neutral-500 dark:text-white/50'>
            {employee.position || ''}{employee.position && employee.department ? ' · ' : ''}{employee.department || ''}
          </p>
        </div>
      </div>

      <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
        <KpiTile label='Attendance Rate' value={employee.attendance.attendanceRate} icon={CalendarDays} tone='green' format={(n) => `${n}%`} />
        <KpiTile label='Task Completion' value={employee.tasks.completionRate} icon={CheckCircle2} tone='blue' format={(n) => `${n}%`} />
        <KpiTile label='Avg Working Hrs' value={employee.attendance.avgWorkingHours} icon={Clock} tone='violet' format={(n) => `${n}h`} />
        <KpiTile label='Availability' value={employee.leaveWfh.availabilityRate} icon={TrendingUp} tone='orange' format={(n) => `${n}%`} />
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-5'>
        <GlassCard>
          <GlassCardHeader>
            <GlassCardTitle>Daily Working Hours</GlassCardTitle>
          </GlassCardHeader>
          <ChartContainer config={attendanceChartConfig} className='h-[250px] w-full'>
            <BarChart data={attendanceChartData}>
              <CartesianGrid strokeDasharray='3 3' stroke='rgba(255,255,255,0.1)' />
              <XAxis dataKey='date' stroke='rgba(255,255,255,0.5)' fontSize={11} />
              <YAxis stroke='rgba(255,255,255,0.5)' fontSize={11} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar dataKey='workingHours' fill='var(--color-workingHours)' radius={[4, 4, 0, 0]} />
            </BarChart>
          </ChartContainer>
          <div className='flex flex-wrap gap-3 mt-2 text-xs text-neutral-500 dark:text-white/50'>
            <span>Present: {employee.attendance.daysPresent} days</span>
            <span>Absent: {data.workingDays - employee.attendance.daysPresent} days</span>
            <span>Avg: {employee.attendance.avgWorkingHours}h/day</span>
          </div>
        </GlassCard>

        <GlassCard>
          <GlassCardHeader>
            <GlassCardTitle>Task Distribution</GlassCardTitle>
          </GlassCardHeader>
          {taskPieData.length > 0 ? (
            <ChartContainer config={taskPieConfig} className='h-[250px] w-full'>
              <PieChart>
                <Pie data={taskPieData} dataKey='value' nameKey='name' cx='50%' cy='50%' outerRadius={80} innerRadius={40}>
                  {taskPieData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Pie>
                <ChartTooltip content={<ChartTooltipContent />} />
              </PieChart>
            </ChartContainer>
          ) : (
            <EmptyState icon={ListTodo} title='No tasks assigned' description='This intern has no tasks in the selected range.' />
          )}
          <div className='flex flex-wrap gap-3 mt-2 text-xs text-neutral-600 dark:text-white/60'>
            <span className='flex items-center gap-1'><span className='w-3 h-3 rounded bg-emerald-500' /> Completed ({employee.tasks.completed})</span>
            <span className='flex items-center gap-1'><span className='w-3 h-3 rounded bg-blue-500' /> In Progress ({employee.tasks.inProgress})</span>
            <span className='flex items-center gap-1'><span className='w-3 h-3 rounded bg-amber-500' /> Pending ({employee.tasks.pending})</span>
          </div>
        </GlassCard>
      </div>

      {hoursChartData.length > 0 && (
        <GlassCard>
          <GlassCardHeader>
            <GlassCardTitle>Weekly Hours</GlassCardTitle>
          </GlassCardHeader>
          <ChartContainer config={hoursChartConfig} className='h-[250px] w-full'>
            <BarChart data={hoursChartData}>
              <CartesianGrid strokeDasharray='3 3' stroke='rgba(255,255,255,0.1)' />
              <XAxis dataKey='week' stroke='rgba(255,255,255,0.5)' fontSize={12} />
              <YAxis stroke='rgba(255,255,255,0.5)' fontSize={12} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar dataKey='hours' fill='var(--color-hours)' radius={[4, 4, 0, 0]} />
            </BarChart>
          </ChartContainer>
        </GlassCard>
      )}

      <div className='grid grid-cols-1 md:grid-cols-2 gap-5'>
        <GlassCard>
          <GlassCardHeader>
            <GlassCardTitle>Detailed Metrics</GlassCardTitle>
          </GlassCardHeader>
          <div className="space-y-1">
            <MetricRow label='Days Present' value={employee.attendance.daysPresent} total={data.workingDays} />
            <MetricRow label='Total Working Hours' value={`${employee.attendance.totalWorkingHours}h`} />
            <MetricRow label='Avg Working Hours' value={`${employee.attendance.avgWorkingHours}h/day`} />
            <MetricRow label='Avg Turnaround' value={`${employee.tasks.avgTurnaroundDays} days`} />
            <MetricRow label='Total Hours Logged' value={employee.dailyWork.totalHours} />
            <MetricRow label='Days with Logs' value={employee.dailyWork.daysWithLogs} total={data.workingDays} />
            <MetricRow label='Consistency' value={`${employee.dailyWork.consistencyRate}%`} />
          </div>
        </GlassCard>

        <GlassCard>
          <GlassCardHeader>
            <GlassCardTitle>Leave & WFH</GlassCardTitle>
          </GlassCardHeader>
          <div className="space-y-1">
            <MetricRow label='Leave Days' value={employee.leaveWfh.leaveDays} />
            <MetricRow label='WFH Days' value={employee.leaveWfh.wfhDays} />
            <MetricRow label='Availability Rate' value={`${employee.leaveWfh.availabilityRate}%`} />
            <MetricRow label='Working Days in Range' value={data.workingDays} />
          </div>
        </GlassCard>
      </div>
    </PageShell>
  )
}

function MetricRow({ label, value, total }: { label: string; value: any; total?: number }) {
  return (
    <div className='flex items-center justify-between py-2 border-b border-neutral-100 dark:border-white/5 last:border-0'>
      <span className='text-neutral-500 dark:text-white/50 text-sm'>{label}</span>
      <span className='text-neutral-900 dark:text-white/90 font-medium tabular-nums'>
        {value}{total !== undefined ? <span className='text-neutral-400 dark:text-white/40 text-sm'>/{total}</span> : null}
      </span>
    </div>
  )
}
